import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { IaAprendizadoWhatsApp, IaLacuna, IaPagamentosWhatsApp, IaWhatsAppPlano, IaChatHistorico, IaChatRequest, IaConfiguracaoCliente, IaContextoRequest, IaResposta, IaContextoComercial, IaScriptPrompt } from '../../models/ia/ia';

@Injectable({ providedIn: 'root' })
export class IaService {
  private baseUrl: string;
  private headers = new HttpHeaders({ "Content-Type": "application/json" });

  constructor(private http: HttpClient, @Inject("BASE_URL") baseUrl: string, private toastr: ToastrService) {
    this.baseUrl = baseUrl;
  }

  private guid(): string { return localStorage.getItem("guidclientenapis"); }
  private sellerId(): number { return parseInt(localStorage.getItem("id"), 10); }
  private fullname(): string { return localStorage.getItem("fullname") || localStorage.getItem("name") || ""; }

  getConfiguration(): Observable<IaConfiguracaoCliente> {
    return this.http.post<IaConfiguracaoCliente>(this.baseUrl + "api/Ia/GetConfiguration", { guidclientenapis: this.guid() }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar a configuracao da IA.");
        return of(new IaConfiguracaoCliente());
      })
    );
  }

  upsertConfiguration(configuracao: IaConfiguracaoCliente): Observable<boolean> {
    configuracao.guidclientenapis = this.guid();
    configuracao.usuario_atualizacao = this.fullname();
    return this.http.post<boolean>(this.baseUrl + "api/Ia/UpsertConfiguration", { configuracao: configuracao }, { headers: this.headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.errorMessage(error) || "Houve um erro ao salvar a configuracao da IA.");
        return of(false);
      })
    );
  }

  getConhecimentosWhatsApp(): Observable<IaScriptPrompt[]> {
    return this.http.post<IaScriptPrompt[]>(this.baseUrl + "api/Ia/GetConhecimentosWhatsApp", { guidclientenapis: this.guid() }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar a base de conhecimento.");
        return of([]);
      })
    );
  }

  salvarConhecimentoWhatsApp(conhecimento: IaScriptPrompt): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + "api/Ia/SalvarConhecimentoWhatsApp", {
      guidclientenapis: this.guid(),
      conhecimento: conhecimento,
      usuario: this.fullname()
    }, { headers: this.headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.errorMessage(error) || "Houve um erro ao salvar o conhecimento.");
        return of(false);
      })
    );
  }

  getAprendizadoWhatsApp(): Observable<IaAprendizadoWhatsApp> {
    return this.http.post<IaAprendizadoWhatsApp>(this.baseUrl + "api/Ia/GetAprendizadoWhatsApp", { guidclientenapis: this.guid() }, { headers: this.headers }).pipe(
      catchError(() => {
        return of(new IaAprendizadoWhatsApp());
      })
    );
  }

  getLacunasWhatsApp(): Observable<IaLacuna[]> {
    return this.http.post<IaLacuna[]>(this.baseUrl + "api/Ia/GetLacunasWhatsApp", { guidclientenapis: this.guid() }, { headers: this.headers }).pipe(
      catchError(() => of([]))
    );
  }

  resolverLacunaWhatsApp(pergunta: string): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + "api/Ia/ResolverLacunaWhatsApp", { guidclientenapis: this.guid(), pergunta: pergunta }, { headers: this.headers }).pipe(
      catchError(() => of(false))
    );
  }

  getPlanosGestaoWhatsApp(): Observable<IaWhatsAppPlano[]> {
    return this.http.post<IaWhatsAppPlano[]>(this.baseUrl + "api/Ia/GetPlanosGestaoWhatsApp", { guidclientenapis: this.guid() }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar os planos.");
        return of([]);
      })
    );
  }

  salvarPlanoWhatsApp(plano: IaWhatsAppPlano): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + "api/Ia/SalvarPlanoWhatsApp", {
      guidclientenapis: this.guid(),
      plano: plano,
      usuario: this.fullname()
    }, { headers: this.headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.errorMessage(error) || "Houve um erro ao salvar o plano.");
        return of(false);
      })
    );
  }

  getPagamentosWhatsApp(): Observable<IaPagamentosWhatsApp> {
    return this.http.post<IaPagamentosWhatsApp>(this.baseUrl + "api/Ia/GetPagamentosWhatsApp", { guidclientenapis: this.guid() }, { headers: this.headers }).pipe(
      catchError(() => of(new IaPagamentosWhatsApp()))
    );
  }

  /* Write-only: os tokens nunca sao lidos de volta, apenas gravados */
  salvarMercadoPagoTokens(publicToken: string, accessToken: string): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + "api/Ia/SalvarMercadoPagoTokens", {
      guidclientenapis: this.guid(),
      public_token: publicToken,
      access_token: accessToken
    }, { headers: this.headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.errorMessage(error) || "Houve um erro ao salvar as credenciais do Mercado Pago.");
        return of(false);
      })
    );
  }

  uploadConhecimentoArquivo(arquivo: File): Observable<IaScriptPrompt> {
    let formData = new FormData();
    formData.append("arquivo", arquivo, arquivo.name);
    formData.append("guidclientenapis", this.guid());

    return this.http.post<IaScriptPrompt>(this.baseUrl + "api/Ia/UploadConhecimentoArquivo", formData).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.errorMessage(error) || "Houve um erro ao enviar o arquivo.");
        return of(null);
      })
    );
  }

  removerConhecimentoWhatsApp(conhecimento: IaScriptPrompt): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + "api/Ia/RemoverConhecimentoWhatsApp", {
      guidclientenapis: this.guid(),
      chave: conhecimento.chave,
      id: conhecimento.id,
      seller_id: conhecimento.seller_id
    }, { headers: this.headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.errorMessage(error) || "Houve um erro ao remover o conhecimento.");
        return of(false);
      })
    );
  }

  getContextoComercial(clientId: number, idCallCenterLigacao: number = null, origem: string = "CALL_CENTER"): Observable<IaContextoComercial> {
    return this.http.post<IaContextoComercial>(this.baseUrl + "api/Ia/GetContextoComercial", this.request(clientId, idCallCenterLigacao, origem), { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao buscar o contexto comercial da IA.");
        return of(new IaContextoComercial());
      })
    );
  }

  getChatHistorico(clientId: number, idCallCenterLigacao: number = null, origem: string = "CALL_CENTER"): Observable<IaChatHistorico[]> {
    return this.http.post<IaChatHistorico[]>(this.baseUrl + "api/Ia/GetChatHistorico", this.request(clientId, idCallCenterLigacao, origem), { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar o historico da IA.");
        return of([]);
      })
    );
  }

  chatCliente(clientId: number, pergunta: string, idCallCenterLigacao: number = null, origem: string = "CALL_CENTER"): Observable<IaResposta> {
    let payload = Object.assign(new IaChatRequest(), this.request(clientId, idCallCenterLigacao, origem));
    payload.pergunta = pergunta;
    return this.http.post<IaResposta>(this.baseUrl + "api/Ia/ChatCliente", payload, { headers: this.headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.errorMessage(error) || "Houve um erro ao consultar a IA.");
        return of(null);
      })
    );
  }

  getUltimaSugestaoProdutos(clientId: number, idCallCenterLigacao: number = null, origem: string = "CALL_CENTER"): Observable<IaResposta> {
    return this.http.post<IaResposta>(this.baseUrl + "api/Ia/GetUltimaSugestaoProdutos", this.request(clientId, idCallCenterLigacao, origem), { headers: this.headers }).pipe(
      catchError(() => of(null))
    );
  }

  sugerirProdutosCliente(clientId: number, idCallCenterLigacao: number = null, origem: string = "CALL_CENTER"): Observable<IaResposta> {
    return this.http.post<IaResposta>(this.baseUrl + "api/Ia/SugerirProdutosCliente", this.request(clientId, idCallCenterLigacao, origem), { headers: this.headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.errorMessage(error) || "Houve um erro ao gerar sugestoes de produtos.");
        return of(null);
      })
    );
  }

  private request(clientId: number, idCallCenterLigacao: number, origem: string): IaContextoRequest {
    let request = new IaContextoRequest();
    request.guidclientenapis = this.guid();
    request.client_id = clientId;
    request.seller_id = this.sellerId();
    request.id_call_center_ligacao = idCallCenterLigacao;
    request.origem = origem;
    return request;
  }

  private errorMessage(error: HttpErrorResponse): string {
    if (error == null || error.error == null) return null;
    if (typeof error.error == "string") return error.error;
    return error.error.message || null;
  }
}
